<div class="main">
    <div class="main-container-wrapper">
        <header class="page-heading">
            <div>
                <span class="eyebrow"><i class="bi bi-cpu" aria-hidden="true"></i> ECU configuration</span>
                <h1>ECU Producers</h1>
                <p>Manage producer names and their accepted alternatives.</p>
            </div>
            <div class="button-container">
                <button type="button" class="refresh-btn" (click)="getCorrectEcuProducers()" aria-label="Refresh ECU Producers" title="Refresh" [disabled]="ecuProducersService.isLoading()">
                    <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
                </button>
                <button type="button" class="add-row-btn" (click)="toggleInputRow()" [attr.aria-expanded]="ecuProducersService.showNewInputRow()">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i> ECU Producer
                </button>
            </div>
        </header>
        <div class="results-panel">
            <div class="toolbar">
                <label class="search-field">
                    <i class="bi bi-search" aria-hidden="true"></i>
                    <input type="search" [value]="searchQuery()" (input)="searchQuery.set($any($event.target).value)"
                           placeholder="Search correct or accepted ECU Producer names" aria-label="Search ECU Producers" />
                </label>
                <span class="result-count" aria-live="polite">{{ filteredProducers().length }} {{ filteredProducers().length === 1 ? 'producer' : 'producers' }}</span>
            </div>
            @defer (when !ecuProducersService.isLoading()) {
                <div class="table-container">
                    <table>
                        <thead><tr>
                            <th scope="col">Correct ECU Producer</th>
                            <th scope="col">Accepted ECU Producers</th>
                            <th scope="col">Actions</th>
                        </tr></thead>
                        <tbody>
                        @if (ecuProducersService.showNewInputRow()) {
                            <tr class="create-row">
                                <td>
                                    <input class="inline-edit-field" type="text" placeholder="Correct ECU Producer"
                                           aria-label="Correct ECU Producer" [formControl]="correctEcuProducer"
                                           (input)="ecuProducersService.newRowConflict.set(null)" />
                                    @if (correctEcuProducer.hasError('duplicateProducer')) {
                                        <small class="new-row-error" role="alert">ECU Producer “{{ correctEcuProducer.value?.trim() }}” already exists.</small>
                                    } @else if (ecuProducersService.newRowConflict()) {
                                        <small class="new-row-error" role="alert">{{ ecuProducersService.newRowConflict() }}</small>
                                    }
                                </td>
                                <td><input class="inline-edit-field" type="text" placeholder="Accepted ECU Producer"
                                           aria-label="Accepted ECU Producer" [formControl]="acceptedEcuProducer" /></td>
                                <td class="actions">
                                    @if (!ecuProducersService.isWaitingForResponse()) {
                                        <button type="button" class="table-buttons" title="Save" aria-label="Save ECU Producer"
                                                [disabled]="correctEcuProducer.invalid || acceptedEcuProducer.invalid || !!ecuProducersService.newRowConflict()"
                                                (click)="saveNewRow()"><i class="bi bi-check-lg" aria-hidden="true"></i></button>
                                        <button type="button" class="table-buttons" title="Cancel" aria-label="Cancel adding ECU Producer"
                                                (click)="ecuProducersService.closeRow()"><i class="bi bi-x-lg" aria-hidden="true"></i></button>
                                    } @else { <span class="saving-label">Saving…</span> }
                                </td>
                            </tr>
                        }
                        @for (element of filteredProducers(); track element.id) {
                            <tr class="body-columns" [class.row-editing]="ecuProducersService.ecuProducerIndex() === originalIndex(element)">
                                <td>
                                    @if (ecuProducersService.ecuProducerIndex() === originalIndex(element)) {
                                        <input class="inline-edit-field" type="text" aria-label="Edit correct ECU Producer"
                                               [formControl]="correctEcuProducer" (keyup.enter)="editEcuProducer(element.id, element)" />
                                    } @else {
                                        <button type="button" class="tag selectable-tag" title="Edit {{ element.correctEcuProducer }}"
                                                (dblclick)="toggleEditView(originalIndex(element), element)">{{ element.correctEcuProducer }}</button>
                                    }
                                </td>
                                <td>
                                    @if (ecuProducersService.ecuProducerIndex() === originalIndex(element)) {
                                        <input class="inline-edit-field" type="text" aria-label="Edit accepted ECU Producers"
                                               [formControl]="acceptedEcuProducer" (keyup.enter)="editEcuProducer(element.id, element)" />
                                    } @else {
                                        <div class="tag-list">
                                            @for (name of visibleAcceptedNames(element); track $index) {
                                                <span class="tag" (dblclick)="toggleEditView(originalIndex(element), element)">{{ name }}</span>
                                            } @empty { <span class="empty-value">—</span> }
                                            @if (acceptedNames(element).length > 3) {
                                                <button type="button" class="more-tag" (click)="toggleAcceptedNames(element.id)">
                                                    {{ expandedAcceptedNames.has(element.id) ? 'Show less' : '+' + (acceptedNames(element).length - 3) + ' more' }}
                                                </button>
                                            }
                                        </div>
                                    }
                                </td>
                                <td class="actions">
                                    @if (ecuProducersService.ecuProducerIndex() === originalIndex(element)) {
                                        @if (ecuProducersService.isWaitingForResponse()) { <span class="saving-label">Saving…</span> }
                                        @else {
                                            <button type="button" class="table-buttons" title="Save" aria-label="Save ECU Producer"
                                                    [disabled]="!correctEcuProducer.value?.trim() || !acceptedEcuProducer.value?.trim()"
                                                    (click)="editEcuProducer(element.id, element)"><i class="bi bi-check-lg" aria-hidden="true"></i></button>
                                            <button type="button" class="table-buttons" title="Cancel" aria-label="Cancel editing ECU Producer"
                                                    (click)="ecuProducersService.closeEditedItem()"><i class="bi bi-x-lg" aria-hidden="true"></i></button>
                                        }
                                    } @else {
                                        <button type="button" class="table-buttons delete-button" title="Delete ECU Producer" aria-label="Delete ECU Producer"
                                                [disabled]="ecuProducersService.isWaitingForResponse()"
                                                (click)="deleteItemFromDatabase(element.id)"><i class="bi bi-trash" aria-hidden="true"></i></button>
                                        <button type="button" class="table-buttons" title="Edit ECU Producer" aria-label="Edit ECU Producer"
                                                [disabled]="!element.id || ecuProducersService.isWaitingForResponse()"
                                                (click)="toggleEditView(originalIndex(element), element)"><i class="bi bi-pencil" aria-hidden="true"></i></button>
                                    }
                                </td>
                            </tr>
                        } @empty {
                            <tr><td colspan="3" class="empty-state"><i class="bi bi-search" aria-hidden="true"></i><strong>No ECU Producers found</strong><span>Try a different search or add a new ECU Producer.</span></td></tr>
                        }
                        </tbody>
                    </table>
                </div>
            } @placeholder (minimum 150ms) {
                <div class="skeleton-wrapper"><app-skeleton-table></app-skeleton-table></div>
            }
        </div>
    </div>
</div>
